import React, { useState } from "react";
import { LogOut, PowerOff, QrCode, Tv, Crown } from "lucide-react";
import { usePartyStore } from "../../stores/partyStore";
import { useAuthStore } from "../../stores/authStore";
import { useIsMobile } from "../../hooks/useIsMobile";
import { leaveParty, endParty } from "../../lib/firebase";
import { QRCodeModal } from "./QRCodeModal";

export const PartyHeader: React.FC = () => {
  const { currentParty, userRole, setTVMode } = usePartyStore();
  const { user, setUser } = useAuthStore();
  const isMobile = useIsMobile();

  const [showQR, setShowQR] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [ending, setEnding] = useState(false);

  const isHost = userRole === "HOST";

  const handleLeaveParty = async () => {
    if (!currentParty || !user) return;
    setLeaving(true);

    try {
      await leaveParty(currentParty.id, user.uid);
      setUser({ ...user, currentPartyId: null });
    } catch (err: any) {
      alert(`Could not leave party: ${err.message}`);
    } finally {
      setLeaving(false);
    }
  };

  const handleEndParty = async () => {
    if (!currentParty) return;
    const confirmed = window.confirm(
      "Are you sure you want to end the party for everyone? This will conclude the session, show the Party Wrapped stats, and return all guests to the lobby."
    );
    if (!confirmed) return;

    setEnding(true);
    try {
      await endParty(currentParty.id);
    } catch (err: any) {
      alert(`Could not end party: ${err.message}`);
    } finally {
      setEnding(false);
    }
  };

  if (!currentParty) return null;

  return (
    <div className="bg-card p-4 sm:p-5 rounded-3xl border border-border shadow-xl space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        {/* Title and Code */}
        <div className="flex items-center gap-3">
          <div className="space-y-0.5">
            <div className="flex items-center gap-2">
              <h2 className="text-2xl sm:text-3xl font-bold font-heading tracking-wide text-text-main">
                {currentParty.partyName}
              </h2>
              {isHost && (
                <span className="bg-primary/20 text-primary text-[10px] font-bold px-2 py-0.5 rounded-md uppercase flex items-center gap-1">
                  <Crown className="w-3 h-3" /> Host
                </span>
              )}
            </div>

            <button
              onClick={() => setShowQR(true)}
              className="inline-flex items-center gap-1.5 text-xs font-mono font-bold text-accent hover:text-accent-hover transition-colors cursor-pointer"
            >
              <QrCode className="w-3.5 h-3.5" />
              <span>Room Code: {currentParty.partyCode} (Tap for QR)</span>
            </button>
          </div>
        </div>

        {/* Header Action Buttons */}
        <div className="flex items-center gap-2">
          {!isMobile && (
            <button
              onClick={() => setTVMode(true)}
              className="py-2 px-3.5 rounded-xl bg-card border border-border hover:border-primary text-text-main text-xs font-semibold flex items-center gap-1.5 transition-all shadow-sm cursor-pointer"
              title="Open TV Screen View"
            >
              <Tv className="w-4 h-4 text-primary" />
              <span>TV Screen</span>
            </button>
          )}

          <button
            onClick={handleLeaveParty}
            disabled={leaving}
            className="py-2 px-3.5 rounded-xl bg-card border border-border hover:border-text-muted text-text-muted hover:text-text-main text-xs font-semibold flex items-center gap-1.5 transition-all cursor-pointer disabled:opacity-50"
            title="Leave party room"
          >
            <LogOut className="w-4 h-4" />
            <span>Leave</span>
          </button>

          {isHost && (
            <button
              onClick={handleEndParty}
              disabled={ending}
              className="py-2 px-3.5 rounded-xl bg-red-500/10 border border-red-500/30 hover:bg-red-500/20 text-red-400 text-xs font-semibold flex items-center gap-1.5 transition-all cursor-pointer disabled:opacity-50"
              title="End party for all guests"
            >
              <PowerOff className="w-4 h-4" />
              <span>{ending ? "Ending..." : "End Party"}</span>
            </button>
          )}
        </div>
      </div>

      <QRCodeModal
        isOpen={showQR}
        onClose={() => setShowQR(false)}
        partyCode={currentParty.partyCode}
      />
    </div>
  );
};
